import React, { useState, FormEvent, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { uploadTenantBulk, TenantUploadResult, BulkTenantUploadResponse } from "../../features/outlet/TenantSlice";
import { AlertCircle, X } from "lucide-react";
import * as XLSX from "xlsx";
import CustomModal from "../common/CustomModal";

/* --------------------------------- Interfaces ------------------------------ */
interface BulkTenantUploadProps {
  principalId: string | number | null;
  programId: string | number | null;
  setShowUploadModal: (showUploadModal: boolean) => void;
  refreshData: () => void;
  showUploadModal: boolean;
}

const BulkTenantUpload: React.FC<BulkTenantUploadProps> = ({ principalId, programId, setShowUploadModal, showUploadModal, refreshData }) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  /* --------------------------------- States ------------------------------ */

  const [formErrors, setFormErrors] = useState({ csvFileError: "", principalIdError: "", programIdError: "" });
  const [uploadResponse, setUploadResponse] = useState<TenantUploadResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset error states
    setFormErrors({
      csvFileError: "",
      principalIdError: "",
      programIdError: "",
    });

    // Validate csvFile
    if (!csvFile) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        csvFileError: "Please upload a XLSX file",
      }));
    }

    // Validate principal_id
    if (!principalId) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        principalIdError: "Please select a Principal",
      }));
    }

    // Validate program_id
    if (!programId) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        programIdError: "Please select a Program",
      }));
    }


    // If the form is invalid, return early
    if (!csvFile || !principalId || !programId) return;

    setLoading(true);
    const response = await dispatch(
      uploadTenantBulk({
        principalId: principalId.toString(),
        programId: programId.toString(),
        xlsxFile: csvFile,
      }),
    );
    if (response.type === "tenant/bulkUpload/fulfilled") {
      const operations = response.payload as BulkTenantUploadResponse;
      setUploadResponse(operations.tenants);
    }
    if (response.type === "tenant/bulkUpload/rejected") {
      const error = response.payload as string;
      setError(error);
    }
    setLoading(false);
    setFormErrors({
      csvFileError: "",
      principalIdError: "",
      programIdError: "",
    });
  };

  const handleDownloadFormat = () => {
    const headers = ["Outlet Name", "property", "wing", "floor", "OutletBrand", "remark", "ContactEmail", "ContactMobile", "ContactPerson", "Mall"];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "tenant_upload_format.xlsx");
  };

  // Handling CSV file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      csvFileError: "",
    }));
    setUploadResponse(null)
    const file = event.target.files?.[0];
    setCsvFile(file || null);
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  if (!showUploadModal) return null;

  // Helper for closing and resetting
  const handleClose = () => {
    setUploadResponse(null);
    setCsvFile(null);
    setShowUploadModal(false);
    refreshData();
  };

  // Helper for rendering upload results
  const renderResultsTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <svg className="w-8 h-8 text-brand animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <span className="ml-3 text-gray-600">Uploading...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 my-4">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      );
    }
    if (uploadResponse && uploadResponse.length > 0) {
      return (
        <div className="overflow-x-auto mt-6">
          <table className="min-w-full text-xs border rounded-lg overflow-hidden">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Failure</th>
                <th className="px-2 py-2">Property</th>
                <th className="px-2 py-2">Wing</th>
                <th className="px-2 py-2">Floor</th>
                <th className="px-2 py-2">Brand</th>
                <th className="px-2 py-2">Remark</th>
                <th className="px-2 py-2">Coordinator</th>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Phone</th>
              </tr>
            </thead>
            <tbody>
              {uploadResponse.map((data, idx) => (
                <tr key={idx} className="border-b last:border-b-0">
                  <td className="px-2 py-1">{data.response?.tenantName || data.payload['Outlet Name']}</td>
                  <td className="px-2 py-1">{data.statusCode}</td>
                  <td className="px-2 py-1">{data.message}</td>
                  <td className="px-2 py-1">{data.response?.property || ((data.payload.property || "") + (data.payload.Mall ? (" " + data.payload.Mall) : ""))}</td>
                  <td className="px-2 py-1">{data.response?.wing || data.payload.wing}</td>
                  <td className="px-2 py-1">{data.response?.floor || data.payload.floor}</td>
                  <td className="px-2 py-1">{data.response?.tenantBrand || data.payload.OutletBrand}</td>
                  <td className="px-2 py-1">{data.response?.remark || data.payload.remark}</td>
                  <td className="px-2 py-1">{data.response?.storeCoordinator || data.payload.ContactPerson}</td>
                  <td className="px-2 py-1">{data.response?.email || data.payload.ContactEmail}</td>
                  <td className="px-2 py-1">{data.response?.phone || data.payload.ContactMobile}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  return (
    <CustomModal
      show={showUploadModal}
      onHide={handleClose}
      title="Bulk Tenant Upload"
      width="w-[500px]"
    >
      <div className="w-full ">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload XLSX File</label>
          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />

          {csvFile && (
            <div className="mt-2 text-xs text-gray-500">Selected file: {csvFile.name}</div>
          )}

        </div>
        {/* Principal/Program/File Errors - grouped */}
        {(formErrors.csvFileError || formErrors.principalIdError || formErrors.programIdError) && (
          <div className="flex flex-col gap-2 text-red-600 bg-red-50  rounded-md px-4 py-3 my-2">
            <div className="space-y-1 text-sm  items-center">
              {formErrors.csvFileError && <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mt-0.5 mr-3" />
                {formErrors.csvFileError && <span>{formErrors.csvFileError}</span>}
              </div>
              }
              {formErrors.principalIdError && <div className="flex items-center">
                {formErrors.principalIdError && <span className="ml-[32px]">{formErrors.principalIdError}</span>}
              </div>
              }
              {formErrors.programIdError && <div className="flex items-center">
                {formErrors.programIdError && <span className="ml-[32px]">{formErrors.programIdError}</span>}
              </div>
              }
            </div>
          </div>
        )}
        {/* Actions */}
        <div className="flex justify-between pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
            onClick={handleDownloadFormat}
          >
            Download Format
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
          >
            Upload
          </button>
        </div>
      </form>
      {/* Results Table */}
      {renderResultsTable()}
      </div>
    </CustomModal>
  );
};

export default BulkTenantUpload;
